import { useState, useMemo } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  Modal,
  Form,
  DatePicker,
  Descriptions,
  Divider,
  Spin,
  Alert,
  Popconfirm,
} from 'antd';
import { PlusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {
  useVigilanceReports,
  useVigilanceById,
  useCreateVigilance,
  useUpdateVigilance,
  useSubmitVigilance,
  useStartVigilanceInvestigation,
  useCloseVigilance,
} from '@hooks/useRegulatory';
import type {
  VigilanceItem,
  VigilanceDetail,
  VigilanceFilters,
  VigilanceSeverity,
  VigilanceStatus,
} from '@/types/regulatory';

const MD_SEVERITY_OPTIONS: { value: VigilanceSeverity | ''; label: string }[] = [
  { value: '', label: 'Всі рівні' },
  { value: 'Critical', label: 'Критичний' },
  { value: 'Major', label: 'Значний' },
  { value: 'Minor', label: 'Незначний' },
];

const MD_STATUS_OPTIONS: { value: VigilanceStatus | ''; label: string }[] = [
  { value: '', label: 'Всі статуси' },
  { value: 'Draft', label: 'Чернетка' },
  { value: 'Submitted', label: 'Поданий' },
  { value: 'UnderInvestigation', label: 'Розслідування' },
  { value: 'Closed', label: 'Закритий' },
];

const MD_SEVERITY_COLOR_MAP: Record<VigilanceSeverity, string> = {
  Critical: 'red',
  Major: 'orange',
  Minor: 'blue',
};

const MD_SEVERITY_LABEL_MAP: Record<VigilanceSeverity, string> = {
  Critical: 'Критичний',
  Major: 'Значний',
  Minor: 'Незначний',
};

const MD_STATUS_COLOR_MAP: Record<VigilanceStatus, string> = {
  Draft: 'blue',
  Submitted: 'green',
  UnderInvestigation: 'orange',
  Closed: 'default',
};

const MD_STATUS_LABEL_MAP: Record<VigilanceStatus, string> = {
  Draft: 'Чернетка',
  Submitted: 'Поданий',
  UnderInvestigation: 'Розслідування',
  Closed: 'Закритий',
};

// timestamptz на сервері приймає лише UTC: дата без часу дала б DateTime Kind=Unspecified
const mdToUtcDate = (mdDate: Dayjs): string => `${mdDate.format('YYYY-MM-DD')}T00:00:00Z`;

// Порожнє поле Guid? сервер не розбере — надсилаємо undefined
const mdOptionalId = (mdValue: string | undefined): string | undefined => mdValue?.trim() || undefined;

const MD_GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type ViewMode = 'list' | 'detail';

export default function VigilancePage() {
  const [mdViewMode, setMdViewMode] = useState<ViewMode>('list');
  const [mdSelectedId, setMdSelectedId] = useState<string | undefined>();
  const [mdSearch, setMdSearch] = useState('');
  const [mdSeverityFilter, setMdSeverityFilter] = useState<VigilanceSeverity | ''>('');
  const [mdStatusFilter, setMdStatusFilter] = useState<VigilanceStatus | ''>('');
  const [mdPage, setMdPage] = useState(1);
  const [mdPageSize, setMdPageSize] = useState(20);
  const [mdModalOpen, setMdModalOpen] = useState(false);
  const [mdCloseModalOpen, setMdCloseModalOpen] = useState(false);
  const [mdSubmitModalOpen, setMdSubmitModalOpen] = useState(false);
  const [mdEditingItem, setMdEditingItem] = useState<VigilanceDetail | null>(null);

  const [mdForm] = Form.useForm();
  const [mdCloseForm] = Form.useForm();
  const [mdSubmitForm] = Form.useForm();

  const mdFilters: VigilanceFilters = useMemo(() => ({
    search: mdSearch || undefined,
    severity: mdSeverityFilter || undefined,
    status: mdStatusFilter || undefined,
    page: mdPage,
    pageSize: mdPageSize,
  }), [mdSearch, mdSeverityFilter, mdStatusFilter, mdPage, mdPageSize]);

  const { data: mdReportsData, isLoading: mdIsLoading } = useVigilanceReports(mdFilters);
  const { data: mdDetailData, isLoading: mdDetailLoading } = useVigilanceById(mdSelectedId);
  const mdCreateMutation = useCreateVigilance();
  const mdUpdateMutation = useUpdateVigilance();
  const mdSubmitMutation = useSubmitVigilance();
  const mdStartInvestigationMutation = useStartVigilanceInvestigation();
  const mdCloseMutation = useCloseVigilance();

  // Порожнє поле не стирає номер, збережений раніше через редагування чернетки
  const mdHandleOpenSubmit = () => {
    setMdSubmitModalOpen(true);
  };

  const mdHandleSubmitReport = async () => {
    const mdValues = await mdSubmitForm.validateFields();
    if (!mdSelectedId) return;
    await mdSubmitMutation.mutateAsync({
      id: mdSelectedId,
      dsmdReportNumber: mdValues.dsmdReportNumber?.trim() || undefined,
    });
    setMdSubmitModalOpen(false);
    mdSubmitForm.resetFields();
  };

  const mdHandleOpenCreate = () => {
    setMdEditingItem(null);
    mdForm.resetFields();
    setMdModalOpen(true);
  };

  const mdHandleOpenEdit = (mdRecord: VigilanceDetail) => {
    setMdEditingItem(mdRecord);
    mdForm.setFieldsValue({
      productId: mdRecord.productId,
      incidentDate: dayjs(mdRecord.incidentDate),
      severity: mdRecord.severity,
      description: mdRecord.description,
      batchId: mdRecord.batchId ?? undefined,
      customerId: mdRecord.customerId ?? undefined,
      dsmdReportNumber: mdRecord.dsmdReportNumber ?? undefined,
    });
    setMdModalOpen(true);
  };

  const mdHandleSubmitForm = async () => {
    const mdValues = await mdForm.validateFields();

    if (mdEditingItem) {
      // Виріб і дату інциденту сервер при оновленні не змінює
      await mdUpdateMutation.mutateAsync({
        id: mdEditingItem.id,
        description: mdValues.description,
        severity: mdValues.severity,
        batchId: mdOptionalId(mdValues.batchId),
        customerId: mdOptionalId(mdValues.customerId),
        dsmdReportNumber: mdValues.dsmdReportNumber?.trim() || undefined,
      });
    } else {
      await mdCreateMutation.mutateAsync({
        productId: mdValues.productId.trim(),
        incidentDate: mdToUtcDate(mdValues.incidentDate),
        severity: mdValues.severity,
        description: mdValues.description,
        batchId: mdOptionalId(mdValues.batchId),
        customerId: mdOptionalId(mdValues.customerId),
      });
    }
    setMdModalOpen(false);
  };

  const mdHandleClose = async () => {
    const mdValues = await mdCloseForm.validateFields();
    if (mdSelectedId) {
      await mdCloseMutation.mutateAsync({
        id: mdSelectedId,
        investigation: mdValues.investigation,
        correctiveAction: mdValues.correctiveAction?.trim() || undefined,
      });
      setMdCloseModalOpen(false);
      mdCloseForm.resetFields();
    }
  };

  const mdHandleViewDetail = (mdId: string) => {
    setMdSelectedId(mdId);
    setMdViewMode('detail');
  };

  const mdHandleBackToList = () => {
    setMdViewMode('list');
    setMdSelectedId(undefined);
  };

  const mdColumns: ColumnsType<VigilanceItem> = [
    {
      title: 'Номер',
      dataIndex: 'number',
      key: 'number',
      width: 140,
      sorter: (mdA, mdB) => mdA.number.localeCompare(mdB.number),
    },
    {
      title: 'Продукт',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
    },
    {
      title: 'Дата інциденту',
      dataIndex: 'incidentDate',
      key: 'incidentDate',
      width: 140,
      render: (mdValue: string) => dayjs(mdValue).format('DD.MM.YYYY'),
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 130,
      render: (mdValue: VigilanceSeverity) => (
        <Tag color={MD_SEVERITY_COLOR_MAP[mdValue]}>
          {MD_SEVERITY_LABEL_MAP[mdValue]}
        </Tag>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (mdValue: VigilanceStatus) => (
        <Tag color={MD_STATUS_COLOR_MAP[mdValue]}>
          {MD_STATUS_LABEL_MAP[mdValue]}
        </Tag>
      ),
    },
    {
      title: '№ звіту ДСМД',
      dataIndex: 'dsmdReportNumber',
      key: 'dsmdReportNumber',
      width: 160,
      render: (mdValue: string | null) => mdValue || '—',
    },
  ];

  const mdEditModal = (
    <Modal
      title={mdEditingItem ? 'Редагувати звіт' : 'Новий звіт vigilance'}
      open={mdModalOpen}
      onOk={mdHandleSubmitForm}
      onCancel={() => setMdModalOpen(false)}
      confirmLoading={mdCreateMutation.isPending || mdUpdateMutation.isPending}
      okText={mdEditingItem ? 'Зберегти' : 'Створити'}
      cancelText="Скасувати"
      width={600}
    >
      <Form form={mdForm} layout="vertical">
        <Form.Item
          name="productId"
          label="Продукт (ID)"
          rules={[
            { required: true, message: 'Введіть ID продукту' },
            { pattern: MD_GUID_PATTERN, message: 'Некоректний ID (очікується GUID)' },
          ]}
        >
          <Input disabled={!!mdEditingItem} />
        </Form.Item>
        <Form.Item
          name="incidentDate"
          label="Дата інциденту"
          rules={[{ required: true, message: 'Оберіть дату інциденту' }]}
        >
          <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} disabled={!!mdEditingItem} />
        </Form.Item>
        <Form.Item
          name="severity"
          label="Severity"
          rules={[{ required: true, message: 'Оберіть рівень серйозності' }]}
        >
          <Select options={MD_SEVERITY_OPTIONS.filter((mdOpt) => mdOpt.value !== '')} />
        </Form.Item>
        <Form.Item
          name="description"
          label="Опис інциденту"
          rules={[{ required: true, message: 'Опишіть інцидент' }]}
        >
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item
          name="batchId"
          label="Партія (ID)"
          rules={[{ pattern: MD_GUID_PATTERN, message: 'Некоректний ID (очікується GUID)' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="customerId"
          label="Клієнт (ID)"
          rules={[{ pattern: MD_GUID_PATTERN, message: 'Некоректний ID (очікується GUID)' }]}
        >
          <Input />
        </Form.Item>
        {/* Номер звіту ДСМД сервер приймає в оновленні чернетки та при поданні */}
        {mdEditingItem && (
          <Form.Item
            name="dsmdReportNumber"
            label="№ звіту ДСМД"
            rules={[{ max: 50, message: 'Не більше 50 символів' }]}
          >
            <Input />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );

  // ── Detail View ────────────────────────────────────────

  if (mdViewMode === 'detail') {
    if (mdDetailLoading) {
      return (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      );
    }

    if (!mdDetailData) {
      return <Alert type="error" message="Звіт не знайдено" showIcon />;
    }

    const mdDetail: VigilanceDetail = mdDetailData;

    return (
      <>
        <Space style={{ marginBottom: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={mdHandleBackToList}>
            Назад до vigilance
          </Button>
        </Space>

        <Typography.Title level={4}>
          Vigilance {mdDetail.number}
        </Typography.Title>

        <Descriptions bordered column={2} size="small" style={{ marginBottom: 24 }}>
          <Descriptions.Item label="Продукт">{mdDetail.productName}</Descriptions.Item>
          <Descriptions.Item label="Дата інциденту">{dayjs(mdDetail.incidentDate).format('DD.MM.YYYY')}</Descriptions.Item>
          <Descriptions.Item label="Severity">
            <Tag color={MD_SEVERITY_COLOR_MAP[mdDetail.severity]}>
              {MD_SEVERITY_LABEL_MAP[mdDetail.severity]}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Статус">
            <Tag color={MD_STATUS_COLOR_MAP[mdDetail.status]}>
              {MD_STATUS_LABEL_MAP[mdDetail.status]}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="№ звіту ДСМД">{mdDetail.dsmdReportNumber ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Партія">{mdDetail.batchNumber ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Клієнт">{mdDetail.customerName ?? '—'}</Descriptions.Item>
        </Descriptions>

        {mdDetail.description && (
          <>
            <Divider orientation="left">Опис інциденту</Divider>
            <Typography.Paragraph>{mdDetail.description}</Typography.Paragraph>
          </>
        )}

        {mdDetail.investigation && (
          <>
            <Divider orientation="left">Розслідування</Divider>
            <Typography.Paragraph>{mdDetail.investigation}</Typography.Paragraph>
          </>
        )}

        {mdDetail.correctiveAction && (
          <>
            <Divider orientation="left">Коригувальні дії</Divider>
            <Typography.Paragraph>{mdDetail.correctiveAction}</Typography.Paragraph>
          </>
        )}

        <Divider />
        {/* Сервер: оновлення і подання — лише з Draft; розслідування — з Submitted; закриття — після подання */}
        <Space>
          {mdDetail.status === 'Draft' && (
            <Button onClick={() => mdHandleOpenEdit(mdDetail)}>
              Редагувати
            </Button>
          )}
          {mdDetail.status === 'Draft' && (
            <Button type="primary" onClick={mdHandleOpenSubmit}>
              Подати до ДСМД
            </Button>
          )}
          {mdDetail.status === 'Submitted' && (
            <Popconfirm
              title="Розпочати розслідування?"
              onConfirm={() => mdStartInvestigationMutation.mutate(mdDetail.id)}
              okText="Розпочати"
              cancelText="Скасувати"
            >
              <Button loading={mdStartInvestigationMutation.isPending}>
                Розпочати розслідування
              </Button>
            </Popconfirm>
          )}
          {(mdDetail.status === 'Submitted' || mdDetail.status === 'UnderInvestigation') && (
            <Button
              type="primary"
              onClick={() => setMdCloseModalOpen(true)}
            >
              Закрити
            </Button>
          )}
        </Space>

        {mdEditModal}

        <Modal
          title="Подати звіт до ДСМД"
          open={mdSubmitModalOpen}
          onOk={mdHandleSubmitReport}
          onCancel={() => setMdSubmitModalOpen(false)}
          confirmLoading={mdSubmitMutation.isPending}
          okText="Подати"
          cancelText="Скасувати"
        >
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Після подання звіт не можна редагувати"
          />
          <Form form={mdSubmitForm} layout="vertical">
            <Form.Item
              name="dsmdReportNumber"
              label="№ звіту ДСМД (якщо вже відомий)"
              rules={[{ max: 50, message: 'Не більше 50 символів' }]}
            >
              <Input />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Закрити звіт vigilance"
          open={mdCloseModalOpen}
          onOk={mdHandleClose}
          onCancel={() => setMdCloseModalOpen(false)}
          confirmLoading={mdCloseMutation.isPending}
          okText="Закрити звіт"
          cancelText="Скасувати"
        >
          <Form form={mdCloseForm} layout="vertical">
            <Form.Item
              name="investigation"
              label="Результати розслідування"
              rules={[{ required: true, message: 'Опишіть результати розслідування' }]}
            >
              <Input.TextArea rows={4} />
            </Form.Item>
            <Form.Item name="correctiveAction" label="Коригувальні дії">
              <Input.TextArea rows={3} />
            </Form.Item>
          </Form>
        </Modal>
      </>
    );
  }

  // ── List View ──────────────────────────────────────────

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Vigilance
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={mdHandleOpenCreate}>
          Новий звіт
        </Button>
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Пошук..."
          allowClear
          onSearch={(mdValue) => {
            setMdSearch(mdValue);
            setMdPage(1);
          }}
          style={{ width: 220 }}
        />
        <Select
          options={MD_SEVERITY_OPTIONS}
          value={mdSeverityFilter}
          onChange={(mdValue) => {
            setMdSeverityFilter(mdValue);
            setMdPage(1);
          }}
          style={{ width: 180 }}
        />
        <Select
          options={MD_STATUS_OPTIONS}
          value={mdStatusFilter}
          onChange={(mdValue) => {
            setMdStatusFilter(mdValue);
            setMdPage(1);
          }}
          style={{ width: 180 }}
        />
      </Space>

      <Table<VigilanceItem>
        columns={mdColumns}
        dataSource={mdReportsData?.items}
        rowKey="id"
        loading={mdIsLoading}
        onRow={(mdRecord) => ({
          onClick: () => mdHandleViewDetail(mdRecord.id),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: mdPage,
          pageSize: mdPageSize,
          total: mdReportsData?.totalCount,
          showSizeChanger: true,
          showTotal: (mdTotal) => `Всього: ${mdTotal}`,
          onChange: (mdNewPage, mdNewPageSize) => {
            setMdPage(mdNewPage);
            setMdPageSize(mdNewPageSize);
          },
        }}
        size="middle"
      />

      {mdEditModal}
    </>
  );
}
